import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import {
  ParamsCreateShare,
  ParamsShare,
  ParamsUpdateShare,
  createShare,
  deleteShare,
  getDetailShare,
  // getShareAccessKey,
  getShares,
  updateShare,
} from '../apis/share.api';
import { queryKey } from '../constants/queryClientKey';

export function useGetShares(
  param: Parameters<typeof getShares>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.share, sortObjectKeys(param)],
    () => getShares(param),
    {
      keepPreviousData: true, //? Keep data when pagination
      ...options,
      onError: (err) => {
        console.error('useGetShares', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getShares>>>;
}

export const useGetShareInfinity = (
  params: Parameters<typeof getShares>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.share_infinite, sortObjectKeys(params)],
    (e) =>
      getShares({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: (lastPage: any, allPages) => {
        if (lastPage.data.data.length < params.limit) return undefined;
        return allPages.reduce((acc, page) => acc + page.data?.data.length, 0);
      },
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getShares>>>;

  const data = queryInfo.data?.pages.map((page) => page.data.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
};

export function useGetDetailShare(
  params: Parameters<typeof getDetailShare>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.share_detail, sortObjectKeys(params)],
    () => getDetailShare(params),
    {
      keepPreviousData: true,
      ...options,
      onError: (err) => {
        console.error('useGetDetailShare', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDetailShare>>>;
}

/* export function useGetAccessKey(id, options) {
  return useQuery(['getAccessKey', id], () => getShareAccessKey(id), {
    keepPreviousData: true, //? Keep data when pagination
    cacheTime: 1000 * 60 * 5, //? 5 minutes

    ...options,
    onError: (err) => {
      console.error('useGetAccessKey', err);
    },
  });
} */

export function useSearchShare() {
  return useMutation(async (params: ParamsShare) => getShares(params), {
    onError: (err) => {
      console.error('useSearchShare', err);
    },
  });
}

export function useCreateShare() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsCreateShare) => createShare(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.share]);
      queryClient.invalidateQueries([queryKey.share_detail]);
    },
    onError: (err) => {
      console.error('useCreateShare', err);
    },
  });
}

export function useUpdateShare() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateShare }) =>
      updateShare(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.share]);
        queryClient.invalidateQueries([queryKey.share_detail]);
      },

      onError: (err) => {
        console.error('useUpdateShares', err);
      },
    }
  );
}

export function useDeleteShare() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteShare(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.share]);
      queryClient.invalidateQueries([queryKey.share_detail]);
    },

    onError: (err) => {
      console.error('useDeleteShares', err);
    },
  });
}
